import React, { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Trash2,
  Copy,
  MapPin,
  AlertCircle,
  Navigation,
  Circle,
  Check,
  Edit,
  Info,
  Map as MapIcon,
  Radio,
  UtensilsCrossed,
} from "lucide-react";
import { getMapboxToken, geocodeAddress } from "@/lib/mapbox";
import { wizardStagesForDay, buildStageIdResolver } from "@/lib/briefingStages";
import { planZoneCopy } from "@/lib/zoneCopyPlan";
import { upsertStageZone } from "@/lib/stageZoneWrite";
import { isInternalStageSlug } from "@/lib/stageLabel";
import { toast } from "sonner";
import {
  buildZoneDrawStyles,
  drawingModeToAreaType,
  ensureZonePillImages,
  zoneHoverPopupHtml,
  zoneLabelLayout,
  zoneLabelPaint,
  zonePillImageId,
} from "@/lib/zoneStyle";
import {
  clearSelectionUnlessDrawing,
  enterDirectSelect,
  resetDrawSelection,
  withSafeSimpleSelect,
} from "@/lib/drawSafeModes";
import { ZonePanelCard } from "@/components/ZonePanelCard";
import { useProducerLocationMarker } from "@/hooks/useProducerLocationMarker";
import { createCirclePolygon, hasStageMapAccess, type ZoneAreaType } from "@produce/shared";
import { zoneServerErrorMessage, zoneShapeMessage } from "@/lib/zoneWriteError";
import { saveZoneShapeUpdates, zoneShapeUpdateFeedback } from "@/lib/zoneShapeUpdate";
import {
  copyShapeRefusal,
  copyWriteFailure,
  zoneCopyReport,
  type ZoneCopyOutcome,
} from "@/lib/zoneCopyReport";
import { useProductionTierAccess } from "@/hooks/useProductionTierAccess";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { saveFestivalBoundary, type BoundaryPolygon } from "@/lib/festivalBoundary";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MapboxDrawEvent } from "@/types/domain";
import { parsePolygon, geometryToJson } from "@/types/domain";
import { buildZoneFeatures, needsSelectionClear, visibleZonesForDay } from "@/lib/zoneMapSync";
import type { Json, Tables } from "@/integrations/supabase/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface Stage {
  name: string;
  label: string;
}

interface LiveTrackingSetupProps {
  navigationAddress: string;
  stages: Stage[];
  /** The date of each festival day, in order (yyyy-MM-dd). */
  festivalDayDates: string[];
  onSave: (data: {
    mapCenter: [number, number];
    mapZoom: number;
    festivalBoundary: GeoJSON.Feature<GeoJSON.Polygon> | null;
    stageZones: GeoJSON.Feature<GeoJSON.Polygon>[];
  }) => void;
  initialData?: {
    mapCenter?: [number, number];
    mapZoom?: number;
    festivalBoundary?: GeoJSON.Feature<GeoJSON.Polygon> | null;
    stageZones?: GeoJSON.Feature<GeoJSON.Polygon>[];
  };
}

export function LiveTrackingSetup({
  navigationAddress,
  stages,
  festivalDayDates,
  onSave: _onSave,
  initialData,
}: LiveTrackingSetupProps) {
  const { id: briefingId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  // Helper to invalidate all festival areas caches after zone changes
  const invalidateFestivalAreasCache = () => {
    // Invalidate with partial matching to catch all day variations
    queryClient.invalidateQueries({
      queryKey: ["festival-areas"],
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: ["festival-areas-preview"],
      exact: false,
    });
  };
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const draw = useRef<MapboxDraw | null>(null);
  const _hasAutoFlown = useRef(false);
  const popup = useRef<mapboxgl.Popup | null>(null);

  const [mapZoom, setMapZoom] = useState(initialData?.mapZoom || 15);
  const [selectedDay, setSelectedDay] = useState(0);
  const selectedDayRef = useRef(0);
  const [drawingMode, setDrawingMode] = useState<
    "boundary" | "stage" | "circle" | "media_office" | "crew_catering" | null
  >(null);
  const drawingModeRef = useRef<
    "boundary" | "stage" | "circle" | "media_office" | "crew_catering" | null
  >(null);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const selectedStageRef = useRef<string | null>(null);

  // Sync refs with state to avoid stale closures in event handlers
  useEffect(() => {
    selectedDayRef.current = selectedDay;
  }, [selectedDay]);

  useEffect(() => {
    drawingModeRef.current = drawingMode;
  }, [drawingMode]);

  useEffect(() => {
    selectedStageRef.current = selectedStage;
  }, [selectedStage]);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [isLoadingMap, setIsLoadingMap] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  const [manualAddress, setManualAddress] = useState(navigationAddress || "");
  const [circleCenter, setCircleCenter] = useState<[number, number] | null>(null);
  const [loadedZones, setLoadedZones] = useState<Tables<"festival_areas">[]>([]);
  const [showHowTo, setShowHowTo] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  // Copying takes a round-trip per zone; without this a second click
  // re-runs the whole batch and doubles every message.
  const [isCopyingZones, setIsCopyingZones] = useState(false);

  // Check plan tier access - include manual_producer_access for free forever licenses
  const { hasAccess } = useProductionTierAccess(briefingId, hasStageMapAccess);

  // Canonical briefing_stages — used to bridge the JSONB-shape
  // `stages` prop (carries only name+label) to the UUID FK. The
  // zone↔stage match below prefers `festival_areas.briefing_stage_id`
  // ↔ `briefing_stages.id` when both sides have it; falls back to
  // the legacy `stage_id` slug match for unbackfilled rows.
  const { data: briefingStageRecords = [] } = useQuery<
    Array<{ id: string; label: string; event_date: string }>
  >({
    queryKey: ["briefing_stages_setup", briefingId],
    enabled: !!briefingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("briefing_stages")
        .select("id, label, event_date")
        .eq("briefing_id", briefingId!);
      if (error) throw error;
      return data ?? [];
    },
  });
  // Per-day resolver, not a label→id map: the same stage label exists
  // once per festival day, and a label-only lookup keeps whichever day
  // happened to be last. Day 2's "Mainstage" zone would then carry day
  // 1's stage id — eight such rows exist in production. Shared with
  // the briefing save path, which learned this the same way.
  const resolveStageId = useMemo(
    () => buildStageIdResolver(briefingStageRecords),
    [briefingStageRecords],
  );

  // Refs for stages + the stage-id resolver because saveStageZoneToDB is
  // called from event handlers (handleDrawCreate) bound by a useEffect
  // with `[hasAccess]` deps — the listener is captured once and never
  // re-bound, so reading `stages` / the resolver directly from
  // the closure picks up whatever they were at first map mount
  // (often empty during the briefing-load race), not their current
  // value. Producer hit this as polygons saved with `name="day0_stage1"`
  // (the synthetic fallback) and `briefing_stage_id=null`.
  const stagesRef = useRef(stages);
  useEffect(() => {
    stagesRef.current = stages;
  }, [stages]);
  const resolveStageIdRef = useRef(resolveStageId);
  useEffect(() => {
    resolveStageIdRef.current = resolveStageId;
  }, [resolveStageId]);
  // Same reason: the producer can still change the festival dates, and
  // the day index only becomes an event_date through them.
  const festivalDayDatesRef = useRef(festivalDayDates);
  useEffect(() => {
    festivalDayDatesRef.current = festivalDayDates;
  }, [festivalDayDates]);

  // Load navigation address from briefing
  useEffect(() => {
    const loadBriefingAddress = async () => {
      if (!briefingId) return;

      const { data } = await supabase
        .from("briefings")
        .select("navigation_address")
        .eq("id", briefingId)
        .single();

      if (data?.navigation_address && !manualAddress) {
        setManualAddress(data.navigation_address);
      }
    };

    loadBriefingAddress();
  }, [briefingId]);

  // Load map settings and zones from database
  useEffect(() => {
    if (!briefingId || !mapInitialized) return;
    loadMapSettingsAndZones();
  }, [briefingId, mapInitialized]);

  const loadMapSettingsAndZones = async () => {
    if (!briefingId || !map.current) return;

    try {
      // Load map settings
      const { data: mapSettings } = await supabase
        .from("map_settings")
        .select("*")
        .eq("briefing_id", briefingId)
        .maybeSingle();

      // Ignore map settings errors

      // Check if map settings have VALID coordinates (not 0,0)
      const hasValidMapSettings =
        mapSettings && (mapSettings.center_latitude !== 0 || mapSettings.center_longitude !== 0);

      if (hasValidMapSettings) {
        const center: [number, number] = [
          mapSettings.center_longitude,
          mapSettings.center_latitude,
        ];
        const zoom = mapSettings.zoom_level || 15;

        map.current.flyTo({
          center,
          zoom,
          duration: 1000,
        });

        setMapZoom(zoom);
      } else if (manualAddress) {
        // No valid map settings, but we have an address - fly to it automatically
        await flyToManualLocation();
      }

      // Load zones
      await fetchZonesFromDB();
    } catch (error) {
      console.error(error);
    }
  };

  // Add hover popup for zones
  useEffect(() => {
    if (!map.current || !mapInitialized) return;

    popup.current = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: "zone-popup",
    });

    const handleMouseMove = (e: mapboxgl.MapLayerMouseEvent) => {
      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const name = feature.properties?.user_name || feature.properties?.name;

        popup.current
          ?.setLngLat(e.lngLat)
          // Escaped: the zone name is producer input and setHTML is innerHTML.
          .setHTML(zoneHoverPopupHtml(name))
          .addTo(map.current!);

        map.current!.getCanvas().style.cursor = "pointer";
      }
    };

    const handleMouseLeave = () => {
      popup.current?.remove();
      if (map.current) {
        map.current.getCanvas().style.cursor = "";
      }
    };

    // Listen to mousemove on multiple gl-draw layers
    const layers = [
      "gl-draw-polygon-fill-inactive.cold",
      "gl-draw-polygon-fill-inactive.hot",
      "gl-draw-polygon-stroke-inactive.cold",
      "gl-draw-polygon-stroke-inactive.hot",
    ];

    layers.forEach((layer) => {
      map.current!.on("mousemove", layer, handleMouseMove);
      map.current!.on("mouseleave", layer, handleMouseLeave);
    });

    return () => {
      if (map.current) {
        layers.forEach((layer) => {
          map.current!.off("mousemove", layer, handleMouseMove);
          map.current!.off("mouseleave", layer, handleMouseLeave);
        });
      }
      popup.current?.remove();
    };
  }, [mapInitialized]);

  const fetchZonesFromDB = async () => {
    if (!briefingId) return;

    try {
      const { data, error } = await supabase
        .from("festival_areas")
        .select("*")
        .eq("briefing_id", briefingId);

      if (error) throw error;

      setLoadedZones(data || []);
      // Drawing happens in the day-aware effect below; this fetch
      // only updates the source-of-truth state.
    } catch (error) {
      console.error(error);
    }
  };

  // A drawn shape that was not saved is still in the Draw store, styled
  // like a saved zone. Both helpers below put the map back to what the
  // database holds, so it never shows a zone that was not saved.

  /**
   * True (and refused) when the server's polygon trigger would refuse this
   * shape too: say why, and put the map back.
   */
  const refuseInvalidShape = async (
    geometry: GeoJSON.Geometry,
    areaType: ZoneAreaType,
  ): Promise<boolean> => {
    const message = zoneShapeMessage(geometry, areaType);
    if (message === null) return false;
    toast.error(message);
    await fetchZonesFromDB();
    return true;
  };

  /** A save that failed: say why when the server refused the shape (22023), and put the map back. */
  const resyncAfterFailedSave = async (error: unknown, areaType: ZoneAreaType) => {
    console.error(error);
    const refusal = zoneServerErrorMessage(error, areaType);
    if (refusal) toast.error(refusal);
    await fetchZonesFromDB();
  };

  // Render polygons on the map filtered by the current selectedDay.
  // The producer-facing view used to show ALL zones across all days
  // simultaneously, which made identical day-0 / day-1 polygons of
  // the same physical stage stack on top of each other (producer
  // reported it as "2 zones over each other"). Mirror the runtime
  // LiveTracking filter: stage_zones for the active day plus the
  // always-on location zones (boundary, media_office, crew_catering).
  useEffect(() => {
    if (!draw.current || !mapInitialized) return;

    // The db id IS the feature id (see lib/zoneMapSync). draw.set() diffs
    // by id: surviving zones are updated in place — their selection and
    // vertex overlays stay valid, where the old deleteAll()+add() cycle
    // deselected everything on every save and left orphaned vertex
    // overlays clickable (Sentry PRODUCE-1Y). Only a selected feature that
    // is about to drop out (day switch, deleted elsewhere) forces a
    // selection clear first.
    const features = buildZoneFeatures(loadedZones, selectedDay);
    const selectedIds: string[] = draw.current.getSelectedIds();
    if (needsSelectionClear(selectedIds, features)) {
      clearSelectionUnlessDrawing(draw.current);
    }
    draw.current.set({ type: "FeatureCollection", features });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedZones, selectedDay, mapInitialized]);

  // Show the producer's own location blue dot while editing zones, so they
  // can see where they're standing. Shared with the Live Tracking view.
  useProducerLocationMarker(map, mapInitialized);

  // Update zone labels on the map when zones change
  useEffect(() => {
    if (!map.current || !mapInitialized || loadedZones.length === 0) return;

    const sourceId = "zone-labels-source";
    const layerId = "zone-labels-layer";

    // Helper to get display name for zone. Post-migration
    // 20260504240000, festival_areas.name is auto-synced from
    // briefing_stages.label via the sync_stage_name_on_label_change
    // trigger, so it's the canonical display label. The JSONB
    // bridge below is only a fallback for rare zones whose name
    // hasn't propagated yet (e.g. rows with NULL briefing_stage_id).
    const getZoneDisplayName = (zone: Tables<"festival_areas">): string => {
      if (zone.area_type === "stage_zone") {
        // isInternalStageSlug: a copy made before this was fixed stored the
        // slug AS the name, and it must not be shown as one here either.
        if (zone.name && !isInternalStageSlug(zone.name)) return zone.name;
        const stage = stages.find((s) => s.name === zone.stage_id);
        return stage?.label || "Stage";
      }
      return zone.name || zone.area_type.replace("_", " ");
    };

    // Calculate centroid of polygon for label placement
    const calculateCentroid = (coordinates: number[][]): [number, number] => {
      let sumLng = 0,
        sumLat = 0;
      const points = coordinates.length - 1; // Last point is duplicate of first
      for (let i = 0; i < points; i++) {
        sumLng += coordinates[i][0];
        sumLat += coordinates[i][1];
      }
      return [sumLng / points, sumLat / points];
    };

    // Only label the zones that are actually drawn on the map for the
    // active day — mirror the polygon visibility filter exactly. Without
    // this, a multi-day festival kept showing leftover stage labels from
    // the other days (their polygons were hidden, but their labels
    // lingered).
    const labelledZones = visibleZonesForDay(loadedZones, selectedDay);

    // Create GeoJSON features with centroids for label placement
    const features = labelledZones.map((zone) => {
      const parsed = parsePolygon(zone.polygon);
      const coords = parsed?.coordinates?.[0] ?? [];
      const centroid = calculateCentroid(coords);

      return {
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: centroid },
        properties: {
          name: getZoneDisplayName(zone),
          pill: zonePillImageId(zone.area_type),
        },
      };
    });

    const geojsonData = {
      type: "FeatureCollection" as const,
      features,
    };

    // Wait for map style to be loaded
    const addLabels = () => {
      if (!map.current) return;

      // Remove existing layer/source if present
      if (map.current.getLayer(layerId)) map.current.removeLayer(layerId);
      if (map.current.getSource(sourceId)) map.current.removeSource(sourceId);

      // Add new source and layer. Labels sit in a rounded pill that
      // matches the live maps; when pills can't be built we fall back
      // to a plain centred label so text is never lost.
      map.current.addSource(sourceId, { type: "geojson", data: geojsonData });
      const pillsReady = ensureZonePillImages(map.current);
      map.current.addLayer({
        id: layerId,
        type: "symbol",
        source: sourceId,
        layout: zoneLabelLayout(pillsReady ? ["get", "pill"] : undefined),
        paint: zoneLabelPaint(),
      });
    };

    if (map.current.isStyleLoaded()) {
      addLabels();
    } else {
      map.current.once("style.load", addLabels);
    }

    return () => {
      if (map.current) {
        if (map.current.getLayer(layerId)) map.current.removeLayer(layerId);
        if (map.current.getSource(sourceId)) map.current.removeSource(sourceId);
      }
    };
  }, [loadedZones, mapInitialized, stages, selectedDay]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current || !hasAccess) return;

    const initMap = async () => {
      try {
        setIsLoadingMap(true);
        setMapError(null);

        const token = await getMapboxToken();
        mapboxgl.accessToken = token;

        // Determine initial center: prioritize geocoding navigation address
        let initialCenter: [number, number] = [5.2913, 52.1326]; // Default to Utrecht

        // If we have initial data with VALID saved map center (not 0,0), use that
        if (
          initialData?.mapCenter &&
          (initialData.mapCenter[0] !== 0 || initialData.mapCenter[1] !== 0)
        ) {
          initialCenter = initialData.mapCenter;
        }
        // Otherwise, try to geocode the navigation address
        else if (navigationAddress) {
          try {
            initialCenter = await geocodeAddress(navigationAddress);
          } catch {
            // Try to use producer's current location
            if (navigator.geolocation) {
              try {
                const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                  navigator.geolocation.getCurrentPosition(resolve, reject);
                });
                initialCenter = [position.coords.longitude, position.coords.latitude];
              } catch {
                // Could not get current location, using default center
              }
            }
          }
        } else if (navigator.geolocation) {
          // No saved center and no address - try to use current location
          try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject);
            });
            initialCenter = [position.coords.longitude, position.coords.latitude];
          } catch {
            // Could not get current location, using default center
          }
        }

        map.current = new mapboxgl.Map({
          container: mapContainer.current ?? document.createElement("div"),
          style: "mapbox://styles/mapbox/satellite-streets-v12",
          center: initialCenter,
          zoom: mapZoom,
          pitch: 0,
        });

        // Add controls - only zoom controls
        const navControl = new mapboxgl.NavigationControl({
          showCompass: false,
          showZoom: true,
          visualizePitch: false,
        });
        map.current.addControl(navControl, "top-right");

        // Initialize drawing tools with custom styles
        draw.current = new MapboxDraw({
          displayControlsDefault: false,
          controls: {
            polygon: false,
            trash: true,
          },
          defaultMode: "simple_select",
          // REQUIRED: without this MapboxDraw does not expose feature
          // properties (area_type / user_area_type) to the style filters,
          // so none of the per-type zone styles ever match and every zone
          // falls back to the neutral dashed "drawing" style. This is what
          // made stages / media office / crew catering render unfilled.
          userProperties: true,
          styles: buildZoneDrawStyles(),
          // Drops clicks on vertices whose feature we already removed from
          // the store, instead of letting Draw crash on the dangling id.
          // `MapboxDraw.modes` (static) is the mode *objects*; the instance's
          // `draw.modes` is the mode *names* — this needs the former.
          modes: withSafeSimpleSelect(MapboxDraw.modes),
        });

        map.current.addControl(draw.current as mapboxgl.IControl);

        // Event listeners
        map.current.on("draw.create", handleDrawCreate);
        map.current.on("draw.update", handleDrawUpdate);
        map.current.on("draw.delete", handleDrawDelete);
        map.current.on("draw.render", handleDrawRender);
        map.current.on("click", handleMapClick);

        // Listen for clicks on zones to select them
        const zoneClickLayers = [
          "gl-draw-polygon-fill-inactive.cold",
          "gl-draw-polygon-fill-inactive.hot",
        ];

        zoneClickLayers.forEach((layer) => {
          map.current!.on("click", layer, (e: mapboxgl.MapLayerMouseEvent) => {
            if (e.features && e.features.length > 0) {
              const feature = e.features[0];
              const dbId = feature.properties?.db_id ? String(feature.properties.db_id) : undefined;
              if (dbId) {
                setSelectedZoneId(dbId);
              }
            }
          });
        });

        map.current.on("error", (_e) => {
          setMapError("Map failed to load. Please try again.");
          setIsLoadingMap(false);
        });

        map.current.once("load", () => {
          setIsLoadingMap(false);
          setMapInitialized(true);
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        setMapError(`Failed to initialize map: ${errorMessage}`);
        setIsLoadingMap(false);
      }
    };

    initMap();

    return () => {
      if (map.current) {
        map.current.off("draw.create", handleDrawCreate);
        map.current.off("draw.update", handleDrawUpdate);
        map.current.off("draw.delete", handleDrawDelete);
        map.current.off("draw.render", handleDrawRender);
        map.current.off("click", handleMapClick);
        map.current.remove();
      }
    };
  }, [hasAccess]);

  // While a new polygon is being drawn it has no zone type yet, so it
  // would fall back to the neutral "drawing" style. Tag it with its
  // area_type on the first render so it immediately draws in its own
  // colour (and the festival boundary as an unfilled dashed frame).
  // Guarded so it only tags fresh, untagged features → no render loop.
  const handleDrawRender = () => {
    if (!draw.current) return;
    const areaType = drawingModeToAreaType(drawingModeRef.current);
    if (!areaType) return;
    // setFeatureProperty is a real MapboxDraw API but is missing from the
    // shipped type definitions, so reach it through a narrow cast.
    const drawApi = draw.current as unknown as {
      setFeatureProperty: (id: string, prop: string, value: string) => void;
    };
    const all = draw.current.getAll();
    all.features.forEach((f) => {
      if (f.geometry?.type !== "Polygon") return;
      if (f.properties?.user_area_type || f.properties?.db_id) return;
      if (f.id === undefined) return;
      drawApi.setFeatureProperty(f.id as string, "user_area_type", areaType);
      drawApi.setFeatureProperty(f.id as string, "area_type", areaType);
    });
  };

  const handleDrawCreate = async (e: MapboxDrawEvent) => {
    const feature = e.features[0];

    // Read from refs to get current values (avoids stale closure from useEffect event listener)
    const currentDrawingMode = drawingModeRef.current;
    const currentSelectedStage = selectedStageRef.current;
    const currentSelectedDay = selectedDayRef.current;

    if (currentDrawingMode === "boundary") {
      await saveBoundaryToDB(feature);
      setDrawingMode(null);
    } else if (currentDrawingMode === "stage" && currentSelectedStage) {
      await saveStageZoneToDB(currentSelectedStage, currentSelectedDay, feature);
      setDrawingMode(null);
      setSelectedStage(null);
    } else if (currentDrawingMode === "media_office") {
      await saveLocationZoneToDB("media_office", "Media Office", feature);
      setDrawingMode(null);
    } else if (currentDrawingMode === "crew_catering") {
      await saveLocationZoneToDB("crew_catering", "Crew Catering", feature);
      setDrawingMode(null);
    }

    if (map.current) {
      setTimeout(() => {
        map.current?.doubleClickZoom.enable();
      }, 100);
    }
  };

  // A vertex edit, or a move — and a move of several selected zones
  // (shift-click, box select) is ONE event carrying all of them. Each is
  // judged and saved on its own; see lib/zoneShapeUpdate.
  const handleDrawUpdate = async (e: MapboxDrawEvent) => {
    if (!briefingId) return;

    const result = await saveZoneShapeUpdates(e.features, async (id, geometry) => {
      const { error } = await supabase
        .from("festival_areas")
        .update({ polygon: geometryToJson(geometry) })
        .eq("id", id);
      return error;
    });

    result.errors.forEach((error) => console.error(error));
    const { toasts, resync } = zoneShapeUpdateFeedback(result);
    toasts.forEach((message) => toast.error(message));
    if (result.saved > 0) invalidateFestivalAreasCache();
    // A refused or failed shape is put back to what the database holds; a
    // saved one is re-read from it.
    if (resync) await fetchZonesFromDB();
  };

  const saveBoundaryToDB = async (feature: GeoJSON.Feature) => {
    if (!briefingId) return;
    if (await refuseInvalidShape(feature.geometry, "festival_boundary")) return;

    try {
      // Atomic upsert via RPC — replaces the previous DELETE-then-INSERT
      // pattern that left a briefing with no boundary on mid-save
      // failure. See apps/web/src/lib/festivalBoundary.ts.
      await saveFestivalBoundary(
        supabase,
        briefingId,
        geometryToJson(feature.geometry) as unknown as BoundaryPolygon,
      );

      // No property stamping: draw.create hands us a GeoJSON COPY, so
      // mutating it never reached the store — the refetch below re-syncs
      // the map from the database, which is the real source of truth.
      await fetchZonesFromDB();
      invalidateFestivalAreasCache();
    } catch (error) {
      await resyncAfterFailedSave(error, "festival_boundary");
    }
  };

  const saveStageZoneToDB = async (stageId: string, dayIndex: number, feature: GeoJSON.Feature) => {
    if (!briefingId) return;
    if (await refuseInvalidShape(feature.geometry, "stage_zone")) return;

    try {
      // Read from refs — handleDrawCreate is bound once on map init
      // and uses a stale closure for any non-ref state. See the ref
      // declarations above.
      const stageName = stagesRef.current.find((s) => s.name === stageId)?.label || stageId;
      // Resolve the canonical briefing_stages.id from the producer's
      // current label so polygons land with the FK populated. The
      // sync_stage_name_on_label_change trigger keeps this FK alive
      // through subsequent renames; stamping it at write-time means
      // a polygon NEVER ships in the orphan state that broke
      // pre-fix renames (briefing_stage_id NULL, name stale).
      const eventDate = festivalDayDatesRef.current[dayIndex] ?? null;
      const briefingStageId = eventDate ? resolveStageIdRef.current(stageName, eventDate) : null;

      await upsertStageZone(supabase, {
        briefingId,
        stageSlug: stageId,
        dayIndex,
        polygon: geometryToJson(feature.geometry) as Json,
        label: stageName,
        briefingStageId,
      });

      await fetchZonesFromDB();
      invalidateFestivalAreasCache();
    } catch (error) {
      await resyncAfterFailedSave(error, "stage_zone");
    }
  };

  // Generic save function for location zones (media office, crew catering, etc.)
  const saveLocationZoneToDB = async (
    areaType: ZoneAreaType,
    displayName: string,
    feature: GeoJSON.Feature,
  ) => {
    if (!briefingId) return;
    if (await refuseInvalidShape(feature.geometry, areaType)) return;

    try {
      // Atomic upsert through the upsert_location_zone RPC. Same
      // PostgREST inference limitation as the stage_zone path: a
      // partial unique index whose WHERE predicate the REST upsert
      // can't carry blocks ON CONFLICT inference (42P10). The RPC
      // writes the explicit `ON CONFLICT … WHERE area_type='<type>'`
      // clause that PG needs.
      const { data: rpcResp, error } = await supabase.rpc("upsert_location_zone", {
        p_briefing_id: briefingId,
        p_area_type: areaType,
        p_polygon: geometryToJson(feature.geometry) as Json,
        p_name: displayName,
      });
      if (error) throw error;
      const insertedId = (rpcResp as { id: string } | null)?.id;
      if (!insertedId) throw new Error("upsert_location_zone: no id returned");

      await fetchZonesFromDB();
      invalidateFestivalAreasCache();
    } catch (error) {
      await resyncAfterFailedSave(error, areaType);
    }
  };

  const handleDrawDelete = async (e: MapboxDrawEvent) => {
    const feature = e.features[0];
    const dbId = feature.properties?.db_id ? String(feature.properties.db_id) : undefined;

    if (dbId && briefingId) {
      try {
        const { error } = await supabase.from("festival_areas").delete().eq("id", dbId);

        if (error) throw error;

        await fetchZonesFromDB();
        invalidateFestivalAreasCache();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const deleteZone = async (dbId: string) => {
    if (!briefingId) return;

    try {
      const { error } = await supabase.from("festival_areas").delete().eq("id", dbId);

      if (error) throw error;

      // Feature id == db id, so the map removal needs no lookup. The
      // refetch below re-syncs anyway; deleting eagerly just avoids a
      // one-roundtrip flash of the removed polygon.
      draw.current?.delete(dbId);

      await fetchZonesFromDB();
      invalidateFestivalAreasCache();
    } catch (error) {
      console.error(error);
    }
  };

  const flyToManualLocation = async () => {
    if (!map.current || !manualAddress.trim()) return;

    try {
      const coords = await geocodeAddress(manualAddress);

      // Fly to location
      map.current.flyTo({
        center: coords,
        zoom: 16,
        duration: 2000,
      });

      setMapZoom(16);
    } catch (error) {
      console.error(error);
    }
  };

  const handleMapClick = (e: mapboxgl.MapMouseEvent) => {
    if (drawingMode === "circle" && selectedStage) {
      const clickedPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];

      if (!circleCenter) {
        // First click - set center
        setCircleCenter(clickedPoint);
      } else {
        // Second click - calculate radius and create circle
        const radius = calculateDistance(circleCenter, clickedPoint);
        const circlePolygon = createCirclePolygon(
          { lng: circleCenter[0], lat: circleCenter[1] },
          radius,
        );

        // Add to map
        if (draw.current) {
          const circleFeature: GeoJSON.Feature = {
            type: "Feature",
            properties: { stage_id: selectedStage, day_index: selectedDay },
            geometry: circlePolygon,
          };
          draw.current.add(circleFeature);
        }

        setDrawingMode(null);
        setSelectedStage(null);
        setCircleCenter(null);
      }
    } else {
      // Check if clicked on a zone to select it
      if (map.current && draw.current && e.features && e.features.length > 0) {
        const feature = e.features[0];
        const dbId = feature.properties?.db_id ? String(feature.properties.db_id) : undefined;
        if (dbId) {
          setSelectedZoneId(dbId);
        }
      }
    }
  };

  const calculateDistance = (point1: [number, number], point2: [number, number]): number => {
    const R = 6371e3; // Earth radius in meters
    const φ1 = (point1[1] * Math.PI) / 180;
    const φ2 = (point2[1] * Math.PI) / 180;
    const Δφ = ((point2[1] - point1[1]) * Math.PI) / 180;
    const Δλ = ((point2[0] - point1[0]) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const startDrawingBoundary = () => {
    if (!draw.current || !map.current) return;

    // CRITICAL: Disable double-click zoom BEFORE changing mode
    map.current.doubleClickZoom.disable();

    setDrawingMode("boundary");

    // Small delay to ensure doubleClickZoom is fully disabled
    setTimeout(() => {
      if (draw.current) {
        draw.current.changeMode("draw_polygon");
      }
    }, 50);
  };

  const startDrawingStage = (stageId: string) => {
    if (!draw.current || !map.current) return;

    // CRITICAL: Disable double-click zoom BEFORE changing mode
    map.current.doubleClickZoom.disable();

    setDrawingMode("stage");
    setSelectedStage(stageId);

    // Small delay to ensure doubleClickZoom is fully disabled
    setTimeout(() => {
      if (draw.current) {
        draw.current.changeMode("draw_polygon");
      }
    }, 50);
  };

  const finishDrawing = async () => {
    if (!draw.current || !map.current) return;

    if (draw.current.getMode() === "draw_polygon") {
      const inProgress = draw.current
        .getAll()
        .features.find(
          (f: GeoJSON.Feature) => !f.properties?.db_id && f.geometry.type === "Polygon",
        );
      const ringLength =
        inProgress?.geometry.type === "Polygon" ? inProgress.geometry.coordinates[0].length : 0;

      // Mid-draw the ring is [p1..pn, floating, p1]: three REAL vertices
      // means length >= 5. Below that, leaving the mode would silently
      // delete the shape (invalid polygon) while the UI still shows
      // drawing state — keep collecting clicks instead.
      if (inProgress && ringLength < 5) return;

      if (inProgress) {
        // The button must behave EXACTLY like a double-click. The ring
        // carries a floating vertex that tracks the cursor — including the
        // whole trip down to this very button, which used to get saved as a
        // spike on the polygon. Leaving the mode runs draw_polygon.onStop:
        // it drops that floating vertex and fires draw.create, so
        // handleDrawCreate saves the clean shape through the same path a
        // double-click takes.
        draw.current.changeMode("simple_select");
        map.current.doubleClickZoom.enable();
        return;
      }
      // No in-progress feature while the MODE says drawing: a refetch
      // landed mid-draw and draw.set() swept the half-placed polygon out
      // of the store (parity with the old deleteAll behaviour). The mode
      // keeps running invisibly against a detached feature; fall through
      // to the reset below so this button recovers the editor instead of
      // no-opping forever.
    }

    // Nothing (valid) to save — Escape aborted the draw, a mid-draw
    // refetch swept it, or this was a stray click. Reset the editor to a
    // clean state. Every real save runs through handleDrawCreate; the
    // manual re-save this tail used to carry duplicated that path for
    // windows that no longer exist.
    await fetchZonesFromDB();
    resetDrawSelection(draw.current);
    setDrawingMode(null);
    map.current.doubleClickZoom.enable();
  };

  const cancelDrawing = () => {
    if (!draw.current || !map.current) return;

    // Get all features before cancelling
    const beforeCancel = draw.current.getAll();

    // Delete any incomplete/unsaved features
    beforeCancel.features.forEach((f: GeoJSON.Feature) => {
      // Delete features that don't have a db_id (meaning they're not saved yet)
      if (!f.properties?.db_id) {
        draw.current?.delete(f.id as string);
      }
    });

    // Only now: leaving draw_polygon fires draw.create for a still-valid
    // in-progress polygon, so the unsaved features above must be gone first
    // or "Cancel" would save the very zone it is cancelling.
    resetDrawSelection(draw.current);
    map.current.doubleClickZoom.enable();
    setDrawingMode(null);
    setSelectedStage(null);
    setCircleCenter(null);
  };

  const startDrawingLocationZone = (mode: "media_office" | "crew_catering") => {
    if (!draw.current || !map.current) return;

    map.current.doubleClickZoom.disable();
    setDrawingMode(mode);

    setTimeout(() => {
      if (draw.current) {
        draw.current.changeMode("draw_polygon");
      }
    }, 50);
  };

  // Enter vertex-edit mode for the (single) zone of a given type.
  // Shared by the boundary / media office / crew catering panels.
  const editZoneByType = (areaType: string) => {
    const zone = loadedZones.find((z) => z.area_type === areaType);
    if (!zone) return;
    enterDirectSelect(draw.current, zone.id);
  };

  // Delete the (single) zone of a given type.
  const deleteZoneByType = (areaType: string) => {
    const zone = loadedZones.find((z) => z.area_type === areaType);
    if (!zone) return;
    deleteZone(zone.id);
  };

  // Get stages for selected day from the stages prop - filter by day index in stage name
  const stagesForDay = wizardStagesForDay(stages || [], selectedDay);
  // The selected day as an event_date, so stage ids resolve per day.
  const selectedDayEventDate = festivalDayDates[selectedDay] ?? null;

  const copyFromPreviousDay = async () => {
    if (selectedDay === 0 || !briefingId || isCopyingZones) return;

    // Get zones from previous day from the database (loadedZones is fetched from DB)
    const previousDayZones = loadedZones.filter(
      (z: Tables<"festival_areas">) =>
        z.area_type === "stage_zone" && z.day_index === selectedDay - 1,
    );

    if (previousDayZones.length === 0) {
      toast.info(`Day ${selectedDay} has no stage zones to copy.`);
      return;
    }

    // Only onto stages that exist on this day — see zoneCopyPlan.
    const plan = planZoneCopy({
      sourceZones: previousDayZones,
      targetDayStages: stagesForDay,
      targetDayIndex: selectedDay,
    });

    setIsCopyingZones(true);
    try {
      // In parallel: each write targets a different stage, so they never
      // contend, and a 14-stage day would otherwise spend seconds walking
      // round-trips one at a time.
      const outcomes = await Promise.all(
        plan.copies.map(async ({ zone, stageSlug, label }): Promise<ZoneCopyOutcome> => {
          const polygon = parsePolygon(zone.polygon);
          if (!polygon) return { kind: "failed" };
          // A shape drawn before the polygon rules is named, not sent.
          const refusal = copyShapeRefusal(polygon, label);
          if (refusal) return refusal;
          try {
            await upsertStageZone(supabase, {
              briefingId,
              stageSlug,
              dayIndex: selectedDay,
              polygon: polygon as unknown as Json,
              label,
              briefingStageId: selectedDayEventDate
                ? resolveStageId(label, selectedDayEventDate)
                : null,
            });
            return { kind: "copied" };
          } catch (error) {
            console.error(error);
            return copyWriteFailure(error, label);
          }
        }),
      );

      // Refresh zones from database
      await fetchZonesFromDB();
      invalidateFestivalAreasCache();

      // One clause per cause (refused / skipped / failed) — see lib/zoneCopyReport.
      const report = zoneCopyReport({
        outcomes,
        skipped: plan.skipped,
        sourceDay: selectedDay,
        targetDay: selectedDay + 1,
      });
      if (report.success) toast.success(report.success);
      if (report.warning) toast.warning(report.warning);
    } catch (error) {
      console.error(error);
      toast.error("Could not copy the zones. Please try again.");
    } finally {
      setIsCopyingZones(false);
    }
  };

  const numDays = Math.max(festivalDayDates.length, 1);

  if (!hasAccess) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            The live map needs an active subscription. You can skip this step and set up your zones
            once your plan is active.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Festival location</h3>
            <Dialog open={showHowTo} onOpenChange={setShowHowTo}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Info className="mr-2 h-4 w-4" />
                  How to use live tracking
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>How to set up live tracking</DialogTitle>
                  <DialogDescription className="space-y-4 pt-4 text-left">
                    <div>
                      <h4 className="mb-2 font-semibold">1. Find Your Location</h4>
                      <p className="text-sm">
                        Enter the festival address and click the navigation button to fly to the
                        location on the map.
                      </p>
                    </div>
                    <div>
                      <h4 className="mb-2 font-semibold">2. Draw Festival Boundary</h4>
                      <p className="text-sm">
                        Click "Draw zone" to start drawing the festival area. Click points around
                        the perimeter, then double-click on the last point or use "Finish zone" to
                        complete. The boundary will appear green.
                      </p>
                    </div>
                    <div>
                      <h4 className="mb-2 font-semibold">3. Draw Stage Zones</h4>
                      <p className="text-sm">
                        For each stage, click "Draw zone" and outline the stage area. These zones
                        will appear in green and help track which crew members are near each stage.
                      </p>
                    </div>
                    <div>
                      <h4 className="mb-2 font-semibold">4. Edit or Delete Zones</h4>
                      <p className="text-sm">
                        Click on a zone on the map to select it, then use "Edit zone" to adjust
                        points by dragging them. Click "Done" when finished editing. Use "Delete
                        zone" to remove a zone completely.
                      </p>
                    </div>
                    <div>
                      <h4 className="mb-2 font-semibold">Tips</h4>
                      <ul className="list-inside list-disc space-y-1 text-sm">
                        <li>Click on any zone to select and highlight it</li>
                        <li>Use the map controls to zoom and rotate the view</li>
                        <li>This step is optional - you can set it up later</li>
                      </ul>
                    </div>
                  </DialogDescription>
                </DialogHeader>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-2">
            <Label htmlFor="manual-address">Address</Label>
            <div className="flex gap-2">
              <Input
                id="manual-address"
                value={manualAddress}
                onChange={(e) => setManualAddress(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    flyToManualLocation();
                  }
                }}
                className="flex-1"
              />
              <Button onClick={flyToManualLocation} size="icon" disabled={!manualAddress.trim()}>
                <Navigation className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {mapError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{mapError}</AlertDescription>
            </Alert>
          )}

          <div className="relative h-[500px] w-full overflow-hidden rounded-lg border bg-muted">
            {isLoadingMap && !mapError && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-background">
                <div className="text-center">
                  <MapPin className="mx-auto mb-2 h-8 w-8 animate-pulse" />
                  <p className="text-sm text-muted-foreground">Loading map...</p>
                </div>
              </div>
            )}

            <div ref={mapContainer} className="absolute inset-0 h-full w-full" />
          </div>
        </div>
      </Card>

      {mapInitialized && (
        <>
          <ZonePanelCard
            areaType="festival_boundary"
            title="Festival boundary"
            description="Draw a polygon around the entire festival area. Click to place points, then click the first point or double-click to close."
            icon={MapIcon}
            hasZone={!!loadedZones.find((z) => z.area_type === "festival_boundary")}
            selected={
              selectedZoneId === loadedZones.find((z) => z.area_type === "festival_boundary")?.id
            }
            isDrawing={drawingMode === "boundary"}
            anyDrawing={drawingMode !== null}
            onStartDraw={startDrawingBoundary}
            onEdit={() => editZoneByType("festival_boundary")}
            onDelete={() => deleteZoneByType("festival_boundary")}
            onFinishDraw={finishDrawing}
            onCancelDraw={cancelDrawing}
            dataGuide="step-7-tip-1"
          />

          <ZonePanelCard
            areaType="media_office"
            title="Media office"
            description="Optionally outline the media office location. Can power navigation features later."
            icon={Radio}
            optional
            hasZone={!!loadedZones.find((z) => z.area_type === "media_office")}
            selected={
              selectedZoneId === loadedZones.find((z) => z.area_type === "media_office")?.id
            }
            isDrawing={drawingMode === "media_office"}
            anyDrawing={drawingMode !== null}
            onStartDraw={() => startDrawingLocationZone("media_office")}
            onEdit={() => editZoneByType("media_office")}
            onDelete={() => deleteZoneByType("media_office")}
            onFinishDraw={finishDrawing}
            onCancelDraw={cancelDrawing}
          />

          <ZonePanelCard
            areaType="crew_catering"
            title="Crew catering"
            description="Optionally outline the crew catering location. Can power navigation features later."
            icon={UtensilsCrossed}
            optional
            hasZone={!!loadedZones.find((z) => z.area_type === "crew_catering")}
            selected={
              selectedZoneId === loadedZones.find((z) => z.area_type === "crew_catering")?.id
            }
            isDrawing={drawingMode === "crew_catering"}
            anyDrawing={drawingMode !== null}
            onStartDraw={() => startDrawingLocationZone("crew_catering")}
            onEdit={() => editZoneByType("crew_catering")}
            onDelete={() => deleteZoneByType("crew_catering")}
            onFinishDraw={finishDrawing}
            onCancelDraw={cancelDrawing}
          />

          <Card className="p-6" data-guide="step-7-tip-2">
            <h3 className="mb-4 text-lg font-semibold">Stage zones</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              Draw zones for each stage to track which stage crew members are near. Click to place
              points, then click on the first point or double-click to close the polygon.
            </p>

            {numDays > 1 && (
              <Tabs
                value={`day-${selectedDay}`}
                onValueChange={(v) => setSelectedDay(parseInt(v.split("-")[1]))}
                className="mb-4"
              >
                <TabsList>
                  {Array.from({ length: numDays }, (_, i) => (
                    // This one picker drives BOTH what the map shows and which
                    // day a new polygon lands on (the separate "Show on map"
                    // preview toggle above the map confused more than it
                    // helped). Locked while drawing: switching day re-syncs
                    // the map, which wipes the half-placed polygon out of the
                    // store and leaves the producer clicking on nothing.
                    <TabsTrigger key={i} value={`day-${i}`} disabled={drawingMode !== null}>
                      Day {i + 1}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}

            {selectedDay > 0 && (
              <div className="mb-4">
                <Button
                  // Locked while drawing, like the day tabs: the copy's
                  // refetch re-syncs the map and would sweep a half-placed
                  // polygon out of the store mid-draw. Also locked when this
                  // day has no stages at all: there would be nothing for a
                  // copied zone to belong to, and the panel below lists
                  // stages, so the copy would land somewhere the producer
                  // cannot see.
                  disabled={drawingMode !== null || stagesForDay.length === 0 || isCopyingZones}
                  variant="outline"
                  size="sm"
                  onClick={copyFromPreviousDay}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy from Day {selectedDay}
                </Button>
              </div>
            )}

            <div className="space-y-3">
              {stagesForDay.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No stages defined yet. Go back to Step 2 to add stages.
                </p>
              ) : (
                stagesForDay.map((stage) => {
                  // FK-first match: briefing_stages.id ↔
                  // festival_areas.briefing_stage_id. Falls through
                  // to the legacy slug match for zones whose FK
                  // hasn't backfilled yet.
                  const fkId = selectedDayEventDate
                    ? resolveStageId(stage.label || stage.name, selectedDayEventDate)
                    : null;
                  const zoneForStage = loadedZones.find(
                    (z) =>
                      z.area_type === "stage_zone" &&
                      z.day_index === selectedDay &&
                      ((fkId && z.briefing_stage_id === fkId) || z.stage_id === stage.name),
                  );
                  const hasZone = !!zoneForStage;
                  const isSelected = selectedZoneId === zoneForStage?.id;

                  return (
                    <div
                      key={stage.name}
                      className={`flex items-center justify-between rounded-lg border p-3 transition-colors ${
                        isSelected ? "bg-primary/5 ring-2 ring-primary" : ""
                      }`}
                    >
                      <div>
                        <p className="font-medium">{stage.label || stage.name}</p>
                        {hasZone ? (
                          <p className="flex items-center text-sm text-green-600 dark:text-green-400">
                            <Check className="mr-1 h-4 w-4" />
                            Zone set
                          </p>
                        ) : (
                          <p className="flex items-center text-sm text-muted-foreground">
                            <Circle className="mr-1 h-3 w-3" />
                            No zone yet
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        {!hasZone ? (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => startDrawingStage(stage.name)}
                            disabled={drawingMode !== null}
                          >
                            Draw Zone
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => enterDirectSelect(draw.current, zoneForStage.id)}
                              disabled={drawingMode !== null}
                            >
                              <Edit className="mr-1 h-4 w-4" />
                              Edit Zone
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => deleteZone(zoneForStage.id)}
                            >
                              <Trash2 className="mr-1 h-4 w-4" />
                              Delete Zone
                            </Button>
                          </>
                        )}

                        {drawingMode === "stage" && selectedStage === stage.name && (
                          <>
                            <Button onClick={finishDrawing} variant="default" size="sm">
                              Finish Zone
                            </Button>
                            <Button variant="outline" size="sm" onClick={cancelDrawing}>
                              Cancel
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
